import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { ImageModifier } from "../../common/ImageModifier";
import { TextModifier } from "../../common/TextModifier";
import {
  Button,
  LinearProgress,
  Typography,
  Box,
  Checkbox,
  Modal,
  FormControlLabel,
  Autocomplete,
  TextField,
  Stack,
  Divider,
  ListItem,
} from "@mui/material";
import {
  useUserContext,
  usePermissionContext,
} from "../../../contexts/ProviderProvider";
import useNotification from "../../../hooks/useNotification";
import {
  insertEquipmentManagerConfig,
  updateEquipmentManagerConfig,
  getGeneralEQMAssetsByTypeForSku,
} from "../../../API/equipmentManager";
import { EQM_TAG_VERSION_1_SUFFIX } from "../../../constants/eqmTags";
import LazyImage from "../../common/LazyImage";
import { calculateBrandStandardsProgress } from "../../../utilities/helpers";
import { CircularProgress } from "../../common_2/DynamicElements/CircularProgress";

export const ConfigDetailedView = ({
  rows,
  open,
  handleClose,
  metadataId,
  configId,
  action,
  refresh,
  setRefresh,
  selectedSku,
  selectedSkuType,
  selectedUniversity,
  similarConfig,
}) => {
  console.log("props for config detailed view", rows)
  const [changes, setChanges] = useState({});
  const [changesURL, setChangesURL] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [insertLoading, setInsertLoading] = useState(false);
  const [generalAssetByType, setGeneralAssetByType] = useState({});
  const [fontOptions, setFontOptions] = useState([]);
  const [compositeOptions, setCompositeOptions] = useState([]);
  const [backIconLinkOptions, setBackIconLinkOptions] = useState([]);
  const [frontIconLinkOptions, setFrontIconLinkOptions] = useState([]);
  const [templateLinkOptions, setTemplateLinkOptions] = useState([]);
  const [jerseyTemplateLinkOptions, setJerseyTemplateLinkOptions] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);


  // New conditional state variables
  const [hasSportTextOnFront, setHasSportTextOnFront] = useState(false);
  const [hasTemplateFileLink, setHasTemplateFileLink] = useState(false);
  const [hasBack, setHasBack] = useState(false);

  const fieldOrder = [
    "front_icon_general_asset_id",
    "front_has_sport_text", // This is now a real field
    "front_text_font_link_general_asset_id",
    "front_text_font_color",
    "front_verified",
    "has_back_icon", // Not a real field; a trigger for has_back
    "has_template_file", // Not a real field; a trigger
    "mockup_template_general_asset_id",
    "mockup_verified",
    "has_back",
  ];

  const notify = useNotification();
  const user = useUserContext();
  const { views } = usePermissionContext();


  const isValidHexCode = (hexCode) => {
    if (!hexCode) return false;
    const hexRegex = /^#?([0-9A-F]{3}){1,2}$/i;
    return hexRegex.test(hexCode);
  };

  const getThumbnailVariantForImage = (img) => {
    if (img.includes("imagedelivery")) {
      return img.replace("/original", "/thumbnail");
    }
    return img;
  };

  // Helper function to get asset name from ID
  const getAssetFromId = (field, assetId) => {

    if (!assetId || !generalAssetByType) return "";

    let assetType = "";
    if (field === "front_icon_general_asset_id") {
      assetType = "Front Asset Icon File";
    } else if (field === "back_icon_general_asset_id") {
      assetType = "Back Asset Icon File";
    } else if (field === "mockup_template_general_asset_id") {
      if (selectedSkuType == "Jersey") {
        assetType = "Jersey Art File";
      } else {
        assetType = "Mockup Template File";
      }
    } else if (
      field === "front_text_font_link_general_asset_id" ||
      field === "back_text_font_general_asset_id"
    ) {
      assetType = "Font File";
    } else if (field === "back_jersey_number_font_general_asset_id") {
      assetType = "Composite Font File";
    }

    if (assetType && generalAssetByType[assetType]) {
      // Find the asset name by ID
      return generalAssetByType?.[assetType]?.[assetId] // allow this to be undefined if the asset that is in this value is not in the suggested return type from the server
    }
    return "";
  };

  const getURLFromAssetId = (field, assetId) => {
    return getAssetFromId(field, assetId)?.asset_url
  }

  const getAssetNameFromId = (field, assetId) => {
    return getAssetFromId(field, assetId)?.name
  }

  // Helper function to get asset ID from name
  const getAssetIdFromName = (field, assetName) => {
    if (!assetName || !generalAssetByType) return null;

    let assetType = "";
    if (field === "front_icon_general_asset_id") {
      assetType = "Front Asset Icon File";
    } else if (field === "back_icon_general_asset_id") {
      assetType = "Back Asset Icon File";
    } else if (field === "mockup_template_general_asset_id") {
      if (selectedSkuType == "Jersey") {
        assetType = "Jersey Art File";
      } else {
        assetType = "Mockup Template File";
      }
    } else if (
      field === "front_text_font_link_general_asset_id" ||
      field === "back_text_font_general_asset_id"
    ) {
      assetType = "Font File";
    } else if (field === "back_jersey_number_font_general_asset_id") {
      assetType = "Composite Font File";
    }

    if (
      assetType &&
      generalAssetByType[assetType] &&
      generalAssetByType[assetType][assetName]
    ) {
      return generalAssetByType[assetType][assetName].idProductGeneralAssets;
    }
    return null;
  };

  // Initialize conditional states based on existing values
  const initializeConditionalStates = () => {
    const frontHasSportText = rows.find(
      (r) => r.name === "front_has_sport_text"
    );
    const hasBackField = rows.find(
      (r) => r.name === "has_back"
    );
    const mockupTemplateAssetId = rows.find(
      (r) => r.name === "mockup_template_general_asset_id"
    );

    // Check if front has sport text
    if (frontHasSportText && frontHasSportText.value) {
      setHasSportTextOnFront(true);
    }

    // Check if template link has value
    if (mockupTemplateAssetId && mockupTemplateAssetId.value) {
      setHasTemplateFileLink(true);
    }

    // Check if has_back is explicitly set, otherwise default to true
    if (hasBackField) {
      setHasBack(!!hasBackField.value);
    } else {
      setHasBack(true);
    }
  };

  // Initialize URLs for existing values
  const initializeExistingURLs = () => {
    const urlFields = [
      "front_icon_general_asset_id",
      "back_icon_general_asset_id",
      "mockup_template_general_asset_id",
      "back_jersey_number_font_general_asset_id",
    ];
    const initialURLs = {};

    urlFields.forEach((field) => {
      const row = rows.find((r) => r.name === field);
      if (row && row.value) {
        const url = getAssetFromId(field, row.value)?.asset_url;
        if (url) {
          initialURLs[field] = url;
        }
      }
    });

    setChangesURL(initialURLs);
  };

  const handleModalClose = () => {
    setChanges({});
    setChangesURL({});
    handleClose();
    setErrorMessage("");
    // Reset conditional states
    setHasSportTextOnFront(false);
    setHasTemplateFileLink(false);
  };

  // Initialize states when modal opens and data is available
  useEffect(() => {
    setInitialLoading(true);
    if (open && rows.length > 0) {
      // The server filters Jersey Art / Composite Font to version-1 assets;
      // include_ids keeps the assets referenced by the existing config values
      // in the response so their name/URL previews still resolve.
      const includeIds = rows
        .filter((r) => r.name.endsWith("_general_asset_id") && r.value)
        .map((r) => parseInt(r.value, 10))
        .filter((id) => !isNaN(id));

      getGeneralEQMAssetsByTypeForSku(
        user,
        [selectedUniversity],
        selectedSkuType,
        { versionedOnly: true, includeIds }
      )
        .then((res) => {
          setGeneralAssetByType(res.data.obj);

          // Set options for different asset types
          const assetTypes = {
            "Font File": setFontOptions,
            "Back Asset Icon File": setBackIconLinkOptions,
            "Front Asset Icon File": setFrontIconLinkOptions,
            "Mockup Template File": setTemplateLinkOptions,
            "Composite Font File": setCompositeOptions,
            "Jersey Art File": setJerseyTemplateLinkOptions,
          };

          Object.entries(assetTypes).forEach(([assetType, setter]) => {
            if (assetType in res.data.obj) {
              const keys = Object.values(res.data.obj[assetType]);
              if (
                assetType === "Composite Font File" ||
                assetType == "Jersey Art File"
              ) {
                setter(keys.filter((key) =>
                key.tags.endsWith(EQM_TAG_VERSION_1_SUFFIX)
              ));
              } else {
                setter(keys);
              }
            } else {
              setter([]);
            }
          });
          initializeConditionalStates();
          initializeExistingURLs();
          setInitialLoading(false);
        })
        .catch((err) => {
          setInitialLoading(false);
          notify("Failed to get assets: " + err)
        });


    }
  }, [open, rows]);

  const validateForm = () => {
    for (const row of rows) {
      if (row.required) {
        const currentValue =
          changes[row.name] !== undefined ? changes[row.name] : row.value;
        if (!currentValue || currentValue === "") {
          return false;
        }
      }
    }
    return true;
  };

  const updateConfig = () => {
    if (!validateForm()) {
      setErrorMessage("* Missing required fields");
      return;
    }
    if (configId == -1 || configId == undefined) {
      setErrorMessage("Invalid config ID");
      return;
    }

    console.log(rows);

    const configDict = { equipment_manager_config_id: configId, has_back: true };
    rows.forEach((row) => {
      let value =
        changes[row.name] !== undefined ? changes[row.name] : row.value;
      configDict[row.name] = value === "" ? null : value;
    });

    // Handle the conditional fields
    configDict["has_front_sport_text"] = hasSportTextOnFront;
    configDict["has_mockup_template_link"] = hasTemplateFileLink;
    configDict["has_back"] = hasBack;

    setInsertLoading(true);
    console.log(configDict);

    updateEquipmentManagerConfig(user, configDict)
      .then((response) => {
        setChanges({});
        setChangesURL({});
        setInsertLoading(false);
        handleClose();
        setRefresh(refresh + 1);
        notify({
          msg: "Configuration updated successfully",
          variant: "success",
        });
      })
      .catch((error) => {
        setInsertLoading(false);
        notify("Failed to update config: " + (error.message || error));
      });
  };

  const addConfig = () => {
    if (!validateForm()) {
      setErrorMessage("* Missing required fields");
      return;
    }
    if (metadataId == -1 || metadataId == undefined) {
      setErrorMessage("Invalid metadata ID");
      return;
    }

    const configDict = { metadata_id: metadataId };
    rows.forEach((row) => {
      configDict[row.name] =
        changes[row.name] !== undefined ? changes[row.name] : null;
    });

    // Handle the conditional fields
    configDict["front_has_sport_text"] = hasSportTextOnFront;
    configDict["has_mockup_template_link"] = hasTemplateFileLink;
    configDict["has_back"] = hasBack;

    setInsertLoading(true);

    insertEquipmentManagerConfig(user, configDict)
      .then((response) => {
        setChanges({});
        setChangesURL({});
        setInsertLoading(false);
        handleClose();
        setRefresh(refresh + 1);
        notify("Configuration added successfully");
      })
      .catch((error) => {
        setInsertLoading(false);
        notify("Failed to add config: " + (error.message || error));
      });
  };

  const setChangesFilter = (value, field) => {
    console.log(value, field);
    setErrorMessage("");
    if (value !== undefined && value !== null) {
      setChanges((prev) => ({ ...prev, [field]: value }));
    } else {
      setChanges((prev) => {
        const newState = { ...prev };
        delete newState[field];
        return newState;
      });
    }
  };

  const setChangesFilterAndURL = (assetId, field) => {
    // Convert asset name to ID if needed

    const url = getURLFromAssetId(field, assetId);

    setErrorMessage("");
    if (assetId !== undefined && assetId !== null) {
      setChanges((prev) => ({ ...prev, [field]: assetId }));
      setChangesURL((prev) => ({ ...prev, [field]: url }));
    } else {
      setChanges((prev) => {
        const newState = { ...prev };
        delete newState[field];
        return newState;
      });
      setChangesURL((prev) => {
        const newState = { ...prev };
        delete newState[field];
        return newState;
      });
    }
  };

  const getColor = (row) => {
    const colorText =
      changes[row.name] !== undefined ? changes[row.name] : row.value;
    if (!colorText) return "#000000";
    return colorText.startsWith("#") ? colorText : "#" + colorText;
  };

  const getCurrentImageURL = (fieldName) => {
    // Check if there's a URL in changesURL first
    if (changesURL[fieldName]) {
      return changesURL[fieldName].replace(
        "https://www.dropbox.com",
        "https://dl.dropboxusercontent.com"
      );
    }

    // Otherwise, try to get URL from current value
    const currentValue =
      changes[fieldName] !== undefined
        ? changes[fieldName]
        : rows.find((r) => r.name === fieldName)?.value || null;

    if (currentValue) {

      const url = getAssetFromId(fieldName, currentValue)?.asset_url;
      return url?.replace(
        "https://www.dropbox.com",
        "https://dl.dropboxusercontent.com"
      );
    }

    return "";
  };

  const autofillData = () => {
    if (!similarConfig) return;

    Object.keys(similarConfig).forEach((key) => {
      if (!key.includes("verified") && !key.includes("_id")) {
        setChangesFilter(similarConfig[key], key);

        // Also set URL if it's an image field
        if (
          [
            "front_icon_general_asset_id",
            "back_icon_general_asset_id",
            "mockup_template_general_asset_id",
            "back_jersey_number_font_general_asset_id",
          ].includes(key)
        ) {
          const url = getURLFromAssetId(key, similarConfig[key]);
          setChangesURL((prev) => ({ ...prev, [key]: url }));
        }
      }
    });

    // Update conditional states based on autofilled data
    if (similarConfig.front_has_sport_text) {
      setHasSportTextOnFront(true);
    }
    if (similarConfig.has_back !== undefined) {
      setHasBack(!!similarConfig.has_back);
    }
    if (similarConfig.mockup_template_general_asset_id) {
      setHasTemplateFileLink(true);
    }
  };

  const renderColorField = (row, index) => (
    <Box key={index} sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <TextModifier
            label={row.label}
            original={row.value}
            value={changes[row.name]}
            setValue={(value) => {
              setChangesFilter(value, row.name);
            }}
            permissions={[views.EQMANAGER]}
            required={row.required}
          />
        </Box>
        <Box
          sx={{
            bgcolor: getColor(row),
            width: 60,
            height: 40,
            border: isValidHexCode(getColor(row)) ? "2px solid" : "2px dashed",
            borderRadius: 1,
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );

  const renderFontField = (row, index) => {
    const currentAssetId =
      changes[row.name] !== undefined ? changes[row.name] : row.value;
    const currentAsset = getAssetFromId(row.name, currentAssetId);

    return (
      <Box key={index} sx={{ mb: 3 }}>
        <Autocomplete
          options={fontOptions}
          value={currentAsset}
          getOptionLabel={x => x.name || x}
          filterOptions={(options, { inputValue }) => {
            if (!inputValue) return options;

            const searchTerm = inputValue.toLowerCase();
            return options.filter(option =>
              option.name?.toLowerCase().includes(searchTerm) ||
              option.tags?.toLowerCase().includes(searchTerm)
            );
          }}
          onChange={(event, newValue) => {
            if (newValue?.idProductGeneralAssets) {
              setChangesFilter(newValue.idProductGeneralAssets, row.name);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={row.label}
              variant="outlined"
              required={row.required}
              fullWidth
            />
          )}
          renderOption={(props, option) => (
            <ListItem
              {...props}
              key={option.idProductGeneralAssets}
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 3fr 75px",
                alignItems: "center",
                paddingY: 1,
                paddingX: 2,
                gap: 2,
                minHeight: "56px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.tags}
              </Typography>
            </ListItem>
          )}
        />
      </Box>
    );
  };

  const renderCompositeFontField = (row, index) => {
    const currentAssetId =
      changes[row.name] !== undefined ? changes[row.name] : row.value;
    const currentAsset = getAssetFromId(row.name, currentAssetId);
    const imageURL = getCurrentImageURL(row.name);

    return (
      <Box key={index} sx={{ mb: 3 }}>
        <Autocomplete
          options={compositeOptions}
          getOptionLabel={x => x.name || x}
          filterOptions={(options, { inputValue }) => {
            if (!inputValue) return options;

            const searchTerm = inputValue.toLowerCase();
            return options.filter(option =>
              option.name?.toLowerCase().includes(searchTerm) ||
              option.tags?.toLowerCase().includes(searchTerm)
            );
          }}
          value={currentAsset}
          onChange={(event, newValue) => {
            if (newValue?.idProductGeneralAssets) {
              setChangesFilterAndURL(newValue.idProductGeneralAssets, row.name);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={row.label}
              variant="outlined"
              required={row.required}
              fullWidth
            />
          )}
          renderOption={(props, option) => (
            <ListItem
              {...props}
              key={option.idProductGeneralAssets}
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 3fr 75px",
                alignItems: "center",
                paddingY: 1,
                paddingX: 2,
                gap: 2,
                minHeight: "56px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.tags}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <LazyImage
                  src={option.asset_url}
                  alt="asset"
                  style={{
                    height: "50px",
                    objectFit: "contain",
                    border: "1px solid #ccc",
                    borderRadius: "2px",
                    padding: "2px",
                    background: "#fff",
                  }}
                />
              </Box>
            </ListItem>
          )}
        />
        {imageURL && (
          <Box sx={{ flexShrink: 0 }}>
            <LazyImage
              src={imageURL}
              alt="Preview"
              style={{
                height: "56px",
                width: "80px",
                objectFit: "contain",
                cursor: "pointer",
                border: "1px solid #ddd",
                borderRadius: "4px",
                padding: "4px",
              }}
              onClick={() => window.open(imageURL, "_blank")}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderImageField = (row, index) => {
    const imageOptions = row.name.includes("front_icon")
      ? frontIconLinkOptions
      : row.name.includes("back_icon")
        ? backIconLinkOptions
        : row.name.includes("template")
          ? selectedSkuType == "Jersey"
            ? jerseyTemplateLinkOptions
            : templateLinkOptions
          : [];

    const currentAssetId =
      changes[row.name] !== undefined ? changes[row.name] : row.value;

    const currentAsset = getAssetFromId(row.name, currentAssetId);
    const imageURL = currentAsset?.asset_url;


    return (
      <Box key={index} sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              label={!currentAsset?.name && currentAssetId ? currentAssetId : ""}
              options={imageOptions}
              getOptionLabel={x => x.name || x}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;

                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  option.name?.toLowerCase().includes(searchTerm) ||
                  option.tags?.toLowerCase().includes(searchTerm)
                );
              }}
              value={currentAsset}
              onChange={(event, newValue) => {
                if (newValue?.idProductGeneralAssets) {
                  setChangesFilterAndURL(
                    newValue.idProductGeneralAssets,
                    row.name
                  );
                }
              }}
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    label={row.label}
                    variant="outlined"
                    required={row.required}
                    fullWidth
                  />
                  {!currentAsset?.name && currentAssetId ? (<Typography variant="h7" color="red">
                    {`${currentAssetId}: the current assset does not fall under the current default asset filter, ignore or contact engineering`} {/* the id of the asset that is to be displayed here is not in the filtered selectable dropdown and therefore can not be displayed, either update the default filter to include this value or update the asset to be included in the filter */}
                  </Typography>) : <></>}
                </>
              )}
              renderOption={(props, option) => (
                <ListItem
                  {...props}
                  key={option.idProductGeneralAssets}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 3fr 75px",
                    alignItems: "center",
                    paddingY: 1,
                    paddingX: 2,
                    gap: 2,
                    minHeight: "56px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {option.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {option.tags}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <img
                      src={getThumbnailVariantForImage(option.asset_url)}
                      alt="asset"
                      style={{
                        height: "50px",
                        objectFit: "contain",
                        border: "1px solid #ccc",
                        borderRadius: "2px",
                        padding: "2px",
                        background: "#fff",
                      }}
                    />
                  </Box>
                </ListItem>
              )}
            />
          </Box>
          {imageURL && (
            <Box sx={{ flexShrink: 0 }}>
              <img
                alt="Preview"
                style={{
                  height: "56px",
                  width: "80px",
                  objectFit: "contain",
                  cursor: "pointer",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "4px",
                }}
                src={imageURL}
                onClick={() => window.open(imageURL, "_blank")}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderBooleanField = (row, index) => {
    const currentValue =
      changes[row.name] !== undefined ? changes[row.name] : row.value;

    return (
      <Box key={index} sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(currentValue)}
              onChange={(e) => {
                setChangesFilter(e.target.checked, row.name);
              }}
            />
          }
          label={row.label}
        />
      </Box>
    );
  };

  const renderField = (row, index, isInConditionalSection = false) => {

    // Skip certain fields that are now handled conditionally (unless we're in the conditional section)
    if (!isInConditionalSection &&
      [
        "front_text_font_link_general_asset_id",
        "front_text_font_color",
        "back_icon_general_asset_id",
        "mockup_template_general_asset_id",
        "has_back", // Skip the has_back field as it's handled in conditional section
      ].includes(row.name)
    ) {
      return null;
    }

    // Handle the front_has_sport_text field as a special case
    if (row.name === "front_has_sport_text") {
      return null; // This is handled in the conditional section
    }

    // Skip back-related fields if hasBack is false (unless we're in the conditional section)
    if (!isInConditionalSection && row.name.startsWith("back_") && !hasBack) {
      return null;
    }

    if (row.type === "Color") {
      return renderColorField(row, index);
    } else if (row.type === "Text" && !row.name.includes("color")) {
      // Check if this is a font field
      if (
        row.name.includes("font") &&
        row.name.includes("idProductGeneralAssets")
      ) {
        return renderFontField(row, index);
      }
      return renderFontField(row, index); // Default to font field for asset IDs
    } else if (row.type === "CompositeFontText") {
      return renderCompositeFontField(row, index);
    } else if (row.type === "ImageText") {
      return renderImageField(row, index);
    } else if (row.type === "Boolean") {
      return renderBooleanField(row, index);
    } else {
      return (
        <Box key={index} sx={{ mb: 3 }}>
          <ImageModifier
            label={row.label}
            original={row.name}
            value={changes[row.label]}
            setValue={(value) => {
              setChangesFilter(value, row.name);
            }}
            permissions={[views.EQMANAGER]}
          />
        </Box>
      );
    }
  };

  const renderConditionalSection = (fieldKey, index) => {
    if (fieldKey === "front_has_sport_text") {
      return (
        <Box key={index} sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasSportTextOnFront}
                onChange={(e) => {
                  setHasSportTextOnFront(e.target.checked);
                  // Clear values if unchecked
                  if (!e.target.checked) {
                    setChangesFilter(
                      null,
                      "front_text_font_link_general_asset_id"
                    );
                    setChangesFilter("", "front_text_font_color");
                  }
                }}
              />
            }
            label="Has Sport Text on Front?"
          />

          {hasSportTextOnFront && (
            <Box sx={{ ml: 4, mt: 2, p: 2, borderRadius: 1 }}>
              <Stack spacing={2}>
                <Autocomplete
                  options={fontOptions}
                  getOptionLabel={x => x.name || x}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;

                    const searchTerm = inputValue.toLowerCase();
                    return options.filter(option =>
                      option.name?.toLowerCase().includes(searchTerm) ||
                      option.tags?.toLowerCase().includes(searchTerm)
                    );
                  }}
                  value={getAssetNameFromId(
                    "front_text_font_link_general_asset_id",
                    changes["front_text_font_link_general_asset_id"] !==
                      undefined
                      ? changes["front_text_font_link_general_asset_id"]
                      : rows.find(
                        (r) =>
                          r.name === "front_text_font_link_general_asset_id"
                      )?.value || null
                  )}
                  onChange={(event, newValue) => {
                    if (newValue?.idProductGeneralAssets) {
                      setChangesFilter(
                        newValue.idProductGeneralAssets,
                        "front_text_font_link_general_asset_id"
                      );
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Front Asset Font Name"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    <ListItem
                      {...props}
                      key={option.idProductGeneralAssets}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2fr 3fr 75px",
                        alignItems: "center",
                        paddingY: 1,
                        paddingX: 2,
                        gap: 2,
                        minHeight: "56px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {option.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {option.tags}
                      </Typography>
                    </ListItem>
                  )}
                />

                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <TextModifier
                      label="Front Asset Text Font Color"
                      original={
                        rows.find((r) => r.name === "front_text_font_color")
                          ?.value
                      }
                      value={changes["front_text_font_color"]}
                      setValue={(value) =>
                        setChangesFilter(value, "front_text_font_color")
                      }
                      permissions={[views.EQMANAGER]}
                      required={false}
                    />
                  </Box>
                  {(changes["front_text_font_color"] ||
                    rows.find((r) => r.name === "front_text_font_color")
                      ?.value) && (
                      <Box
                        sx={{
                          bgcolor:
                            changes["front_text_font_color"] !== undefined
                              ? changes["front_text_font_color"].startsWith("#")
                                ? changes["front_text_font_color"]
                                : "#" + changes["front_text_font_color"]
                              : rows
                                .find((r) => r.name === "front_text_font_color")
                                ?.value?.startsWith("#")
                                ? rows.find(
                                  (r) => r.name === "front_text_font_color"
                                )?.value
                                : "#" +
                                rows.find(
                                  (r) => r.name === "front_text_font_color"
                                )?.value,
                          width: 60,
                          height: 40,
                          border: "2px solid",
                          borderRadius: 1,
                          flexShrink: 0,
                        }}
                      />
                    )}
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      );
    } else if (fieldKey === "has_back_icon") {
      return (
        <Box key={index} sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasBack}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setHasBack(newValue);

                  // Store the has_back value in changes
                  setChangesFilter(newValue, "has_back");

                  // Clear all back-related fields if unchecked
                  if (!newValue) {
                    setChangesFilter(null, "back_text_font_color");
                    setChangesFilter(null, "back_jersey_number_font_color");
                    setChangesFilter(null, "back_number_outline_color");
                    setChangesFilter(null, "back_icon_general_asset_id");
                    setChangesFilter(null, "back_text_font_general_asset_id");
                    setChangesFilter(null, "back_jersey_number_font_general_asset_id");
                    setChangesFilter(null, "back_verified");

                    // Clear URLs for image fields
                    setChangesURL((prev) => {
                      const newState = { ...prev };
                      delete newState["back_icon_general_asset_id"];
                      delete newState["back_jersey_number_font_general_asset_id"];
                      return newState;
                    });
                  }
                }}
              />
            }
            label="Has Back?"
          />

          {hasBack && (
            <Box sx={{ ml: 3, mt: 1.5, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
              <Stack spacing={2}>
                {/* Render all back-related fields */}
                {[
                  "back_text_font_color",
                  "back_jersey_number_font_color",
                  "back_number_outline_color",
                  "back_icon_general_asset_id",
                  "back_text_font_general_asset_id",
                  "back_jersey_number_font_general_asset_id",
                  "back_verified"
                ].map(fieldName => {
                  const row = rows.find(r => r.name === fieldName);
                  if (!row) return null;
                  return renderField(row, fieldName, true);
                })}
              </Stack>
            </Box>
          )}
        </Box>
      );
    } else if (fieldKey === "has_template_file") {
      return (
        <Box key={index} sx={{ mb: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={true} disabled />}
            label="Has Template File Link?"
          />

          <Box sx={{ ml: 4, mt: 2, p: 2, borderRadius: 1 }}>
            <Autocomplete
              options={
                selectedSkuType != "Jersey"
                  ? templateLinkOptions
                  : jerseyTemplateLinkOptions
              }
              getOptionLabel={x => x.name || x}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;

                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  option.name?.toLowerCase().includes(searchTerm) ||
                  option.tags?.toLowerCase().includes(searchTerm)
                );
              }}
              value={getAssetFromId(
                "mockup_template_general_asset_id",
                changes["mockup_template_general_asset_id"] !== undefined
                  ? changes["mockup_template_general_asset_id"]
                  : rows.find(
                    (r) => r.name === "mockup_template_general_asset_id"
                  )?.value || null
              )}
              onChange={(event, newValue) => {
                if (newValue?.idProductGeneralAssets) {
                  setChangesFilterAndURL(
                    newValue.idProductGeneralAssets,
                    "mockup_template_general_asset_id"
                  );
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template File Link"
                  variant="outlined"
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <ListItem
                  {...props}
                  key={option.idProductGeneralAssets}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 3fr 75px",
                    alignItems: "center",
                    paddingY: 1,
                    paddingX: 2,
                    gap: 2,
                    minHeight: "56px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {option.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {option.tags}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <img
                      src={getThumbnailVariantForImage(option.asset_url)}
                      alt="asset"
                      style={{
                        height: "50px",
                        objectFit: "contain",
                        border: "1px solid #ccc",
                        borderRadius: "2px",
                        padding: "2px",
                        background: "#fff",
                      }}
                    />
                  </Box>
                </ListItem>
              )}
            />
            {getCurrentImageURL("mockup_template_general_asset_id") && (
              <Box sx={{ mt: 1 }}>
                <img
                  alt="Template Preview"
                  style={{
                    maxHeight: "60px",
                    maxWidth: "100px",
                    objectFit: "contain",
                    cursor: "pointer",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    padding: "4px",
                  }}
                  src={getCurrentImageURL("mockup_template_general_asset_id")}
                  onClick={() =>
                    window.open(
                      getCurrentImageURL("mockup_template_general_asset_id"),
                      "_blank"
                    )
                  }
                />
              </Box>
            )}
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
      }}
    >
      {(loading || initialLoading) ? <CircularProgress /> :
        <Card
          sx={{
            backgroundColor: "background.default",
            width: "90vw",
            maxWidth: "800px",
            maxHeight: "90vh",
            overflow: "auto",
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              {/* Header Section */}
              <Box>
                <Typography variant="h5" component="div" gutterBottom>
                  {action === "Update" ? "Update" : "Add"} Equipment Manager
                  Configuration
                </Typography>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    University: {" "}
                    <Typography
                      component="span"
                      sx={{ fontWeight: "bold", color: "success.main" }}
                      display="inline"
                    >
                      {selectedUniversity}
                    </Typography>
                  </Typography>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Id: {" "}
                      <Typography
                        component="span"
                        sx={{ fontWeight: "bold", color: "success.main" }}
                        display="inline"
                      >
                        {configId}
                      </Typography>
                    </Typography>
                  </Box>               </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    SKU Name:{" "}
                    <Typography
                      component="span"
                      sx={{ fontWeight: "bold", color: "success.main" }}
                      display="inline"
                    >
                      {selectedSku}
                    </Typography>
                  </Typography>
                </Box>

                {similarConfig && Object.keys(similarConfig).length > 0 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={autofillData}
                    sx={{ mt: 2 }}
                  >
                    AUTOFILL CONFIG
                  </Button>
                )}

                {errorMessage && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    {errorMessage}
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* Form Fields */}
              <Box>
                {fieldOrder.map((fieldKey, index) => {
                  // Handle conditional sections
                  if (
                    [
                      "front_has_sport_text",
                      "has_back_icon",
                      "has_template_file",
                    ].includes(fieldKey)
                  ) {
                    return renderConditionalSection(fieldKey, index);
                  }

                  // Skip conditional fields if their parent condition is false
                  if (
                    (fieldKey === "front_text_font_link_general_asset_id" ||
                      fieldKey === "front_text_font_color") &&
                    !hasSportTextOnFront
                  ) {
                    return null;
                  }

                  if (
                    fieldKey === "back_icon_general_asset_id" &&
                    !hasBack
                  ) {
                    return null;
                  }

                  if (
                    fieldKey === "mockup_template_general_asset_id" &&
                    !hasTemplateFileLink
                  ) {
                    return null;
                  }

                  const row = rows.find((r) => r.name === fieldKey);
                  if (!row) return <span>couldnt find the field {fieldKey} in {JSON.stringify(rows)}</span>;

                  return renderField(row, index);
                })}
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                <Button
                  variant="contained"
                  disabled={insertLoading}
                  onClick={() => {
                    action === "Update" ? updateConfig() : addConfig();
                  }}
                  sx={{ minWidth: 120 }}
                >
                  {insertLoading ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>}
    </Modal>
  );
};
